import { useState, useEffect } from "react";
import {
  BookOpen, ChevronRight, GitBranch, Clock, Tag, FileText,
  Lightbulb, AlertCircle, CheckCircle, XCircle, Loader2
} from "lucide-react";
import api from "../api";
import type { JournalEntry, ProjectSummary } from "../api";

// ─── Area badge colors ──────────────────────────────────────────
const AREA_COLORS: Record<string, string> = {
  auth: "#818cf8",
  billing: "#f59e0b",
  db: "#10b981",
  api: "#3b82f6",
  frontend: "#ec4899",
  infra: "#8b5cf6",
  testing: "#06b6d4",
};

const OUTCOME_CONFIG: Record<string, { color: string; icon: typeof CheckCircle; label: string }> = {
  merged: { color: "#10b981", icon: CheckCircle, label: "Merged" },
  failed: { color: "#ef4444", icon: XCircle, label: "Failed" },
  abandoned: { color: "#6b7280", icon: AlertCircle, label: "Abandoned" },
};

function AreaBadge({ area }: { area: string }) {
  const color = AREA_COLORS[area] || "#6b7280";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        backgroundColor: `${color}20`,
        color,
        border: `1px solid ${color}40`,
      }}
    >
      {area}
    </span>
  );
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const config = OUTCOME_CONFIG[outcome] || OUTCOME_CONFIG.abandoned;
  const Icon = config.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        backgroundColor: `${config.color}20`,
        color: config.color,
        border: `1px solid ${config.color}40`,
      }}
    >
      <Icon size={12} />
      {config.label}
    </span>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatCost(costStr: string | null): string {
  if (!costStr) return "—";
  const n = parseFloat(costStr);
  return isNaN(n) ? "—" : `$${n.toFixed(4)}`;
}

// ─── Entry Detail Panel ─────────────────────────────────────────

function EntryDetail({ entry, onBack }: { entry: JournalEntry; onBack: () => void }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <button
          onClick={onBack}
          style={{
            background: "none", border: "none", color: "var(--text-dim)",
            cursor: "pointer", padding: "4px 8px", borderRadius: 4,
            fontSize: 13,
          }}
        >
          ← Back
        </button>
      </div>

      {/* Title */}
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          {entry.outcome && <OutcomeBadge outcome={entry.outcome} />}
          {entry.pr_number && (
            <a
              href={entry.pr_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--accent)", fontSize: 13, textDecoration: "none" }}
            >
              <GitBranch size={13} style={{ marginRight: 4 }} />
              PR #{entry.pr_number}
            </a>
          )}
          <span style={{ color: "var(--text-dim)", fontSize: 12 }}>
            <Clock size={12} style={{ marginRight: 4 }} />
            {formatDate(entry.created_at)}
          </span>
        </div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "var(--text)" }}>
          {entry.issue_title || entry.summary.substring(0, 80)}
        </h3>
      </div>

      {/* Summary */}
      <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Summary
        </div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text)" }}>
          {entry.summary}
        </p>
      </div>

      {/* Root Cause */}
      {entry.root_cause && (
        <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#ef4444", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <AlertCircle size={12} style={{ marginRight: 4 }} />
            Root Cause
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text)" }}>
            {entry.root_cause}
          </p>
        </div>
      )}

      {/* Approach */}
      {entry.approach && (
        <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Approach
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text)" }}>
            {entry.approach}
          </p>
        </div>
      )}

      {/* Alternatives */}
      {entry.alternatives && (
        <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Alternatives Considered
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text)" }}>
            {entry.alternatives}
          </p>
        </div>
      )}

      {/* Lessons */}
      {entry.lessons && (
        <div style={{ padding: 16, borderRadius: 8, background: "#f59e0b10", border: "1px solid #f59e0b30" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#f59e0b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <Lightbulb size={12} style={{ marginRight: 4 }} />
            Lessons Learned
          </div>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: "var(--text)" }}>
            {entry.lessons}
          </p>
        </div>
      )}

      {/* Files & Areas */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Files */}
        {entry.files_touched && entry.files_touched.length > 0 && (
          <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <FileText size={12} style={{ marginRight: 4 }} />
              Files Touched ({entry.files_touched.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {entry.files_touched.map((f, i) => (
                <code key={i} style={{ fontSize: 12, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
                  {f}
                </code>
              ))}
            </div>
          </div>
        )}

        {/* Areas */}
        {entry.areas && entry.areas.length > 0 && (
          <div style={{ padding: 16, borderRadius: 8, background: "var(--surface)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              <Tag size={12} style={{ marginRight: 4 }} />
              Areas
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {entry.areas.map((a, i) => <AreaBadge key={i} area={a} />)}
            </div>
          </div>
        )}
      </div>

      {/* Metadata */}
      <div style={{ display: "flex", gap: 24, fontSize: 12, color: "var(--text-dim)", paddingTop: 8, borderTop: "1px solid var(--border)" }}>
        {entry.tokens_used && <span>Credits: {(entry.tokens_used / 1000).toFixed(1)}</span>}
        <span>Cost: {formatCost(entry.cost_usd)}</span>
        <span>Run: <code style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{entry.run_id?.substring(0, 8) ?? "—"}</code></span>
      </div>
    </div>
  );
}

// ─── Entry List Item ────────────────────────────────────────────

function EntryRow({ entry, onClick }: { entry: JournalEntry; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 16px",
        borderRadius: 8,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        cursor: "pointer",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}
    >
      {/* Left: outcome + summary */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          {entry.outcome && <OutcomeBadge outcome={entry.outcome} />}
          {entry.pr_number && (
            <span style={{ fontSize: 12, color: "var(--accent)" }}>PR #{entry.pr_number}</span>
          )}
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{formatDate(entry.created_at)}</span>
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.issue_title || entry.summary.substring(0, 100)}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.summary.substring(0, 120)}
        </div>
      </div>

      {/* Right: areas + arrow */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {entry.areas && entry.areas.slice(0, 3).map((a, i) => <AreaBadge key={i} area={a} />)}
        <ChevronRight size={14} style={{ color: "var(--text-dim)" }} />
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────

export function JournalPage({ projects }: { projects: ProjectSummary[] }) {
  const [selectedProject, setSelectedProject] = useState<string>(projects[0]?.project_id || "");
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [filterArea, setFilterArea] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    setError(null);
    setSelectedEntry(null);
    (api as any).journal.list(selectedProject, 50)
      .then((data: JournalEntry[]) => setEntries(data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedProject]);

  // Collect all unique areas for filtering
  const allAreas = [...new Set(entries.flatMap(e => e.areas || []))].sort();

  const filteredEntries = filterArea
    ? entries.filter(e => e.areas?.includes(filterArea))
    : entries;

  if (selectedEntry) {
    return (
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 0" }}>
        <EntryDetail entry={selectedEntry} onBack={() => setSelectedEntry(null)} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 0" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BookOpen size={20} style={{ color: "var(--accent)" }} />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--text)" }}>Project Journal</h2>
          <span style={{ fontSize: 12, color: "var(--text-dim)", background: "var(--surface)", padding: "2px 8px", borderRadius: 10 }}>
            {filteredEntries.length} entries
          </span>
        </div>

        {/* Project selector */}
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "6px 12px",
            fontSize: 13,
            color: "var(--text)",
            cursor: "pointer",
          }}
        >
          {projects.map((p) => (
            <option key={p.project_id} value={p.project_id}>{p.display_name}</option>
          ))}
        </select>
      </div>

      {/* Area filter pills */}
      {allAreas.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
          <button
            onClick={() => setFilterArea(null)}
            style={{
              padding: "4px 10px",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 500,
              cursor: "pointer",
              border: "1px solid var(--border)",
              background: filterArea === null ? "var(--accent)" : "var(--surface)",
              color: filterArea === null ? "#fff" : "var(--text-dim)",
            }}
          >
            All
          </button>
          {allAreas.map((area) => (
            <button
              key={area}
              onClick={() => setFilterArea(filterArea === area ? null : area)}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
                border: `1px solid ${filterArea === area ? (AREA_COLORS[area] || "var(--border)") : "var(--border)"}`,
                background: filterArea === area ? `${AREA_COLORS[area] || "var(--accent)"}20` : "var(--surface)",
                color: filterArea === area ? (AREA_COLORS[area] || "var(--accent)") : "var(--text-dim)",
              }}
            >
              {area}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-dim)" }}>
          <Loader2 size={20} className="spinner" style={{ marginBottom: 8 }} />
          <div>Loading journal entries...</div>
        </div>
      )}

      {error && (
        <div style={{ padding: 16, borderRadius: 8, background: "#ef444420", border: "1px solid #ef444440", color: "#ef4444", fontSize: 13 }}>
          {error}
        </div>
      )}

      {!loading && !error && filteredEntries.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-dim)" }}>
          <BookOpen size={32} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 14 }}>No journal entries yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            Journal entries are created automatically when pipeline runs complete.
          </div>
        </div>
      )}

      {!loading && !error && filteredEntries.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filteredEntries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              onClick={() => setSelectedEntry(entry)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
